/**
 * J1FederatedModelView — Wave CH.2 · Federated J.1 3D Model
 *
 * Aggregates 3D-renderable items from every plant module (civil,
 * structural, process, piping, electrical, infrastructure) and
 * renders them in a single Babylon scene with per-layer visibility
 * toggles, layer-coloured legend, and a stat sidebar.
 *
 * Backend: GET /api/projects/{id}/j1/federated-model
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Plant3DTab } from "./ProcessPlantA6View";
import { Layers, AlertCircle, Loader2, Eye, EyeOff } from "lucide-react";

const COL = {
   bg:     "#020617", panel:  "#0f1729", border: "#1e293b",
   text:   "#e2e8f0", dim:    "#94a3b8", spark:  "#22d3ee",
   err:    "#ef4444",
};

const mono = { fontFamily: "'JetBrains Mono', monospace" };

export default function J1FederatedModelView({ projectId }) {
   const [data, setData] = useState(null);
   const [loading, setLoading] = useState(true);
   const [err, setErr] = useState(null);
   const [activeLayers, setActiveLayers] = useState(() =>
      new Set(["civil", "structural", "process",
                  "piping", "electrical", "infrastructure"]));

   useEffect(() => {
      if (!projectId) return;
      let alive = true;
      setLoading(true); setErr(null);
      api.get(`/projects/${projectId}/j1/federated-model`)
         .then((r) => { if (alive) setData(r.data); })
         .catch((e) => {
            if (alive) {
               setErr(e?.response?.data?.detail
                       || e?.message || "Failed to load federated model");
            }
         })
         .finally(() => alive && setLoading(false));
      return () => { alive = false; };
   }, [projectId]);

   // Compose Plant3DTab geometry from active layers only
   const geometry = useMemo(() => {
      if (!data) return null;
      const plot_width_m  = data.plot_width_m  || 200;
      const plot_height_m = data.plot_height_m || 120;
      const layers = data.layers || {};
      const equipment = [];
      const pipe_racks = [];
      const zones = [];

      // Helper: ensure every equipment item has the fields _buildEquipMesh
      // expects (width_m, depth_m, height_m, radius_m, color).
      const normalize = (it, defaultColor) => {
         const d = it.diameter_m || it.w || 4;
         return {
            ...it,
            width_m:  it.width_m  || it.w || d,
            depth_m:  it.depth_m  || it.d || d,
            height_m: it.height_m || it.h || 6,
            radius_m: it.radius_m || (d / 2),
            color:    it.color || defaultColor,
         };
      };

      // Civil layer → plot slab rendered as a flat colored ground patch
      // (gives the plot a visible boundary; otherwise the federated
      // viewer shows only an empty grey plate filled with fog).
      if (activeLayers.has("civil") && layers.civil) {
         layers.civil.forEach((it) => {
            if (it.equip_type === "slab") {
               zones.push({
                  name: "plot",
                  x: it.x || 0, z: it.z || 0,
                  width_m: it.w || plot_width_m,
                  depth_m: it.d || plot_height_m,
               });
            }
         });
      }
      // Process layer → equipment
      if (activeLayers.has("process") && layers.process) {
         layers.process.forEach((e) => equipment.push(normalize(e, "#22d3ee")));
      }
      // Structural layer → racks + buildings
      if (activeLayers.has("structural") && layers.structural) {
         layers.structural.forEach((it) => {
            if (it.equip_type === "rack") {
               pipe_racks.push({
                  polyline: [
                     [it.x - it.w / 2, it.y, it.z],
                     [it.x + it.w / 2, it.y, it.z],
                  ],
                  height_m: it.h,
                  width_m:  it.d,
               });
            } else {
               equipment.push(normalize({
                  ...it, equip_type: "building",
               }, "#94a3b8"));
            }
         });
      }
      // Electrical / Infrastructure → equipment
      if (activeLayers.has("electrical") && layers.electrical) {
         layers.electrical.forEach((it) =>
            equipment.push(normalize(it, "#f59e0b")));
      }
      if (activeLayers.has("infrastructure") && layers.infrastructure) {
         layers.infrastructure.forEach((it) =>
            equipment.push(normalize(it, "#22c55e")));
      }
      // Piping → skipped (no Babylon pipe primitive yet; future work)

      // CH.2 — frame camera around the actual equipment cluster (not
      // the empty corners of the plot) so individual items remain
      // readable. Falls back to the plot when no equipment is active.
      let extent = Math.max(plot_width_m, plot_height_m) * 0.5;
      let cx = 0, cz = 0;
      const all = [...equipment,
         ...pipe_racks.flatMap(r => r.polyline.map(p =>
            ({ x: p[0], z: p[2], width_m: r.width_m || 2,
                depth_m: r.width_m || 2 })))];
      if (all.length) {
         let minX =  Infinity, maxX = -Infinity;
         let minZ =  Infinity, maxZ = -Infinity;
         all.forEach((eq) => {
            const w = (eq.width_m  || 2) / 2;
            const d = (eq.depth_m  || 2) / 2;
            minX = Math.min(minX, eq.x - w);
            maxX = Math.max(maxX, eq.x + w);
            minZ = Math.min(minZ, eq.z - d);
            maxZ = Math.max(maxZ, eq.z + d);
         });
         extent = Math.max(maxX - minX, maxZ - minZ) / 2 + 10;
         cx = (minX + maxX) / 2;
         cz = (minZ + maxZ) / 2;
      }

      return {
         equipment, pipe_racks, zones,
         plot_width_m, plot_height_m,
         // CH.2 — explicit camera radius & target so the framing
         // tracks the equipment, not the (much larger) plot.
         viewOptions: {
            groundPadding: 600,
            cameraRadius: extent * 2.0,
            cameraBeta: Math.PI / 4,
            cameraTarget: [cx, 5, cz],
            fogStartFactor: 8,
            fogEndFactor: 20,
         },
      };
   }, [data, activeLayers]);

   const toggleLayer = (key) => {
      setActiveLayers((s) => {
         const next = new Set(s);
         if (next.has(key)) next.delete(key); else next.add(key);
         return next;
      });
   };

   if (loading) {
      return (
         <div data-testid="j1-federated-loading"
               style={{ padding: 24, color: COL.dim, ...mono, fontSize: 12 }}>
            <Loader2 size={14} style={{ display: "inline",
               animation: "spin 1s linear infinite", marginRight: 6 }}/>
            Loading federated 3D model…
         </div>
      );
   }
   if (err) {
      return (
         <div data-testid="j1-federated-error"
               style={{ padding: 24, color: COL.err, ...mono, fontSize: 12 }}>
            <AlertCircle size={14} style={{ display: "inline",
                                                   marginRight: 6 }}/>
            {err}
         </div>
      );
   }
   if (!data) return null;

   const layerSummary = data.layer_summary || {};

   return (
      <div data-testid="j1-federated-view"
            style={{ display: "flex", gap: 8, height: "calc(100vh - 240px)",
                        minHeight: 600 }}>
         {/* Layer side-panel */}
         <div data-testid="j1-layer-panel"
               style={{ width: 220, padding: 12, background: COL.panel,
                           border: `1px solid ${COL.border}`, borderRadius: 4,
                           ...mono, color: COL.text, fontSize: 11,
                           overflow: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6,
                              marginBottom: 12, fontWeight: 700,
                              color: COL.spark }}>
               <Layers size={14}/> LAYERS
            </div>
            {Object.entries(layerSummary).map(([key, info]) => {
               const on = activeLayers.has(key);
               const Icon = on ? Eye : EyeOff;
               return (
                  <div key={key}
                         data-testid={`j1-layer-${key}`}
                         onClick={() => toggleLayer(key)}
                         style={{ display: "flex", alignItems: "center",
                                     gap: 8, padding: "6px 8px",
                                     marginBottom: 4, borderRadius: 3,
                                     cursor: "pointer",
                                     background: on ? "rgba(34,211,238,0.08)"
                                                            : "transparent",
                                     border: `1px solid ${
                                        on ? COL.spark : COL.border}`,
                                     opacity: on ? 1 : 0.5,
                                     userSelect: "none" }}>
                     <span style={{ width: 12, height: 12, borderRadius: 2,
                                         background: info.color,
                                         flexShrink: 0 }}/>
                     <span style={{ flex: 1, fontSize: 11,
                                         color: COL.text }}>
                        {info.label}
                     </span>
                     <span style={{ fontSize: 10, color: COL.dim }}>
                        {info.count}
                     </span>
                     <Icon size={11} color={on ? COL.spark : COL.dim}/>
                  </div>
               );
            })}
            <div style={{ marginTop: 16, paddingTop: 12,
                              borderTop: `1px solid ${COL.border}`,
                              fontSize: 10, color: COL.dim }}>
               <div data-testid="j1-total-items">
                  Total items: <span style={{ color: COL.text,
                                                      fontWeight: 700 }}>
                                          {data.total_items || 0}</span>
               </div>
               <div style={{ marginTop: 4 }}>
                  Plot: {(data.plot_width_m || 0).toFixed(0)} ×{" "}
                  {(data.plot_height_m || 0).toFixed(0)} m
               </div>
               {!data.has_d2 && (
                  <div style={{ marginTop: 8, padding: 6,
                                     background: "rgba(245, 158, 11, 0.1)",
                                     color: "#f59e0b", borderRadius: 3,
                                     fontSize: 10 }}>
                     ⚠ Run Stage D.2 first — federated model uses
                     placeholder layout until plot plan is generated.
                  </div>
               )}
            </div>
         </div>
         {/* 3D canvas */}
         <div data-testid="j1-3d-canvas-wrap"
               style={{ flex: 1, minHeight: 0, position: "relative",
                           background: COL.bg, borderRadius: 4,
                           overflow: "hidden",
                           border: `1px solid ${COL.border}` }}>
            <Plant3DTab geometry={geometry} />
         </div>
      </div>
   );
}
